import React, { useMemo } from 'react';
import classNames from "classnames";
import { useParams } from 'react-router-dom';

import useTranslationModules, { useTranslate } from "../../../../../i18n/useTranslationModules";
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import { getDefaultAgendaImage } from "../../../../../store/utils/create-event";
import { AgendaLayoutTypesClassic, TemplateClassNames } from "../../../../../types/template-layouts";
import {
	Session,
	SessionTypesFeatures,
	EventSessionTabs,
	NavMainItems,
	IHomepageMainNavItems,
	EPreviewTypes,
	SessionPreview,
	PageModuleType,
} from "../../../../../types/working-model";
import { getHomepageDefaultLanguage } from "../../../../../utils/get-language";
import { OptionalComponent } from "../../../../../utils/optional-component";
import { capitalize, filterSessionsByAgendaModuleSettings, getTemplateClassName, sessionsFromVodOrder } from "../../../../../utils/utils";
import Icon, { COLORS, ICONS } from "../../../../general-ui/icon";
import { Tooltip } from "../../../../general-ui/tooltip/tooltip";
import { ParamsProps } from "../../../../live-event/live-event";
import Footer from "../../../../live-event/marketing-page/footer/footer";
import SessionCard from "../../../../live-event/modules/agenda/agenda-session-card";
import { SessionFilter } from "../../../../live-event/modules/agenda/session-filter";
import MainEventBannerLive from "../../../../live-event/modules/main-event-banner-live/main-event-banner-live";
import AgendaPreviewModal from "../editor/preview/agenda-preview";
import { PostRegisterSideBar } from "./main-preview";
import { useIsNewNavigation } from "../../../../../hooks/navigation.hooks";
import TabHeader from '../../../../general-ui/tab-header/tab-header';
import { CalendarLayout } from '../../../../live-event/modules/agenda/calendar-layout';

import './session-items-list.scss';

interface IEventAgendaItemsProps {
	eventMainNavItems: IHomepageMainNavItems[];
}

const SessionItemsList: React.FC<IEventAgendaItemsProps> = ({ eventMainNavItems }) => {
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const previewMode = useTypedSelector(state => state.CreateEventReducer.previewMode);
	const vodOrder = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings.vodOrder);
	const displayLiveBanner = useTypedSelector(state => state.CreateEventReducer.displayLiveBanner);
	const isNewNavigation= useIsNewNavigation();

	const myListActive = workingEvent?.settings.personalizedAttendeeList;
	const agendaModule = workingEvent?.homepage?.post_register_home_modules.find(module => module.type === PageModuleType.agenda);
	const agendaLayout = agendaModule?.content.layout_type;
	const isCalendarLayout = agendaLayout === AgendaLayoutTypesClassic.calendar;

	const { tab, language } = useParams<ParamsProps>();
	const { t } = useTranslate("homepage");
	const LIVE_SESSIONS = t(`Live sessions`);
	const ON_DEMAND = t(`On demand sessions`);
	const MY_LIST = t(`My list`);

	const calendarLayoutTabs = [t(`homepage:All sessions`)];
	if (myListActive) calendarLayoutTabs.push(t(`homepage:My list`));

	const baseLanguage = getHomepageDefaultLanguage(workingEvent);

	useTranslationModules({ language: language ?? baseLanguage });

	const activeTab = useMemo(() => {
		return tab && capitalize(tab).replaceAll("-", " ");
	}, [tab]);

	const showOnDemandSessions = agendaModule?.content?.show_on_demand_sessions ?? true;
	const showBroadcastSessions = agendaModule?.content?.show_broadcast_sessions ?? true;

	// filter sessions based on agenda module settings
	const _sessions = workingEvent?.sessions;
	const sessions = useMemo(() => {
		return filterSessionsByAgendaModuleSettings({
			sessions: _sessions ?? [],
			showOnDemandSessions,
			showBroadcastSessions,
		});
	}, [_sessions, showOnDemandSessions, showBroadcastSessions]) as Session[];

	const template = getTemplateClassName(workingEvent?.template.name);
	const logo = workingEvent?.homepage?.header.logo;
	// get all sessions that have start time
	const [timedSessions, onDemandSessions] = useMemo(() => {
		if (sessions) {
			let timedSessionsArray: Session[] = [];
			let onDemandSessionsArray: Session[] = [];
			for (const session of sessions) {
				if (SessionTypesFeatures[session.session_type].has_start_time) {
					timedSessionsArray = [...timedSessionsArray, session];
				} else {
					onDemandSessionsArray = [...onDemandSessionsArray, session];
				}
			}
			return [timedSessionsArray, onDemandSessionsArray];
		} else {
			return [[], []];
		}
	}, [sessions]);

	const renderHeader = () => {
		return (
			<div>
				<h6>{t("Sessions")}</h6>
				{/* if multiple session types, then render tabs and show the currently active one */}
				{!!timedSessions.length && !!onDemandSessions.length && !isCalendarLayout && (
					<div className="preview-session-list-tabs">
						<div className={classNames({ 'active-tab': activeTab === EventSessionTabs.EventAgenda })}>{LIVE_SESSIONS}</div>
						<div className={classNames({ 'active-tab': activeTab === EventSessionTabs.OnDemand })}>{ON_DEMAND}</div>
						{myListActive &&
							<div className={classNames({ 'active-tab': activeTab === EventSessionTabs.MyList })}>{MY_LIST}</div>
						}
					</div>
				)}
			</div>
		);
	};


	const agenda = workingEvent?.homepage?.modules.find(m => m.type === PageModuleType.agenda);
	// main event banner module is created by default when a homepage is created
	const mainEventBanner = workingEvent?.homepage?.post_register_home_modules?.find(m => m.type === PageModuleType.main_event_banner);
	const liveSessionList = sessions?.filter(session => SessionTypesFeatures[session.session_type].has_start_time);

	const renderSearch = () => {
		// if event agenda, show full search filters
		if (activeTab === EventSessionTabs.EventAgenda && sessions && agenda) {
			const forcingType = sessions as unknown;
			const _sessions = forcingType as SessionPreview[];
			//this is not actually being used in session filter as it is currently decorative
			return (
				<div className="agenda-sessions-header">
					<SessionFilter
						isLive={true}
						module={agenda}
						sessions={_sessions}
						template={template}
						onFilterChange={() => null}
						overrides={''}
					/>
				</div>
			);
		}
	};

	const sortedTimedSessions = useMemo(() => () => {
		if (timedSessions.length) {
			const sortedByDates = timedSessions.sort((a, b) => {
				if (a.timestamp && b.timestamp) {
					return a.timestamp - b.timestamp;
				}
				return 0;
			});
			return sortedByDates;
		}
		return [];
	}, [timedSessions]);

	const sessionCard = (session: Session, type: EventSessionTabs, idx: number) => {
		if (type === EventSessionTabs.EventAgenda && !session.timestamp) return null;
		// stylingOverrides not being applied in this instance of SessionCard
		return <SessionCard {...{ session, template, defaultImage: getDefaultAgendaImage(template, idx) }} />;
	};

	const cardStyling = () => {
		const newEventLayoutClass = isNewNavigation ? 'agenda-ltr' : 'agenda-standard';
		
		if (isCalendarLayout) {
			return 'agenda-calendar';
		}
		if (template === TemplateClassNames.O2) {
			return 'agenda-vertical_card';
		}
		return newEventLayoutClass;
	};

	if (!sessions.length) return null;

	const activeCustomPages = workingEvent?.custom_pages?.filter(page => page.is_on);

	const sortedSessions = useMemo(() => {
		const sorted = sessionsFromVodOrder<Session>(onDemandSessions, vodOrder);
		return sorted;
	}, [onDemandSessions, vodOrder]);

	const renderSessions = (): JSX.Element => {
		if (activeTab === EventSessionTabs.EventAgenda) {
			return (<>
				{sortedTimedSessions().map((session, idx) => (
					<React.Fragment key={session.session}>{sessionCard(session, EventSessionTabs.EventAgenda, idx)}</React.Fragment>
				))}
			</>);
		}

		if (activeTab === EventSessionTabs.OnDemand) {
			return (<>
				{sortedSessions.map((session: Session, idx: number) => (
					<React.Fragment key={session.session}>{sessionCard(session, EventSessionTabs.OnDemand, idx)}</React.Fragment>
				))}
			</>);
		}

		return (<></>);
	};

	return (
		<>
			<PostRegisterSideBar
				disableProfileBubble
				{...{ eventMainNavItems, template, logo }}
				activeMenuItem={NavMainItems.Sessions}
				customPages={activeCustomPages}
			/>
			<div className={classNames("preview-session-list-container event-agenda agenda", cardStyling(), template, { "display-live-banner": displayLiveBanner })}>
				<OptionalComponent display={displayLiveBanner}>
					<>
						<Tooltip tooltip="This banner will display when a session is live" position="bottom-left" >
							<Icon name={ICONS.PRIMARY_TOOLTIP} size={16} color={`${COLORS.CYAN} tooltip-icon`} />
						</Tooltip>
						{mainEventBanner && (
							<MainEventBannerLive
								module={mainEventBanner}
								template={template}
								key={mainEventBanner?.id}
								liveSessionList={liveSessionList ?? []}
								isAgendaPageEditor
							/>
						)}
					</>
				</OptionalComponent>
				<div className={classNames("preview-session-list-header", {
					'multi-session-types-header': timedSessions.length && onDemandSessions.length
				})}>
					<div className="header-content">
						{renderHeader()}
					</div>
				</div>
				<div className={classNames("preview-session-list-content", template)}>
					{isCalendarLayout && agendaModule ? (
						<>
							<OptionalComponent display={calendarLayoutTabs.length > 1}>
								<TabHeader
									tabs={calendarLayoutTabs}
									currentTab={calendarLayoutTabs[0]}
									template={template}
									shouldTransformText={false}
									hasDivider={true}
								/>
							</OptionalComponent>
							
							<CalendarLayout
								module={agendaModule}
								template={template}
								sessions={sessions}
								language={language}
								editor={true}
								isAgendaPage={true}
								userSavedSessions={workingEvent?.settings?.personalizedAttendeeList ? [] : undefined}
							/>
						</>
					) : (
						<>
							{renderSearch()}
							<div className="agenda-sessions-list">
								{renderSessions()}
							</div>
						</>
					)}
				</div>
				{workingEvent?.homepage?.footer ? (
					<Footer footer={workingEvent.homepage.footer} template={template} />
				) : null}
			</div>
			<AgendaPreviewModal open={previewMode === EPreviewTypes.Agenda} />
		</>
	);
};

export default SessionItemsList;